import { ANOMALY_TYPES, ART_OFFSET_MINUTES, detectAnomalies, splitMinutesByDate } from './timesheet-policy.js';
import { formatMesLargo } from '../utils/formatters.js';

/**
 * Pure UI policy for the Fichadas admin section's employee-scoped listing
 * (owner request, 2026-09-02: "see ALL the fichadas of one employee"). No
 * I/O, no DOM — same split as `js/shared/liquidacion-ui-policy.js`.
 *
 * Deliberately NOT added to `js/shared/timesheet-policy.js` (which already
 * hosts `detectAnomalies`/`splitMinutesByDate`/`formatElapsed`): this is
 * Fichadas-admin-LIST display policy (how many rows, how much time, in the
 * set currently shown), not the payroll/audit anomaly engine that module
 * carries. It does not need a parity twin in `api/_lib/` either — nothing
 * server-side ever summarizes a fichada list for display, same reasoning
 * `formatElapsed`'s own docstring already gives for staying client-only.
 */

/**
 * Exact worked minutes for one fichada row, reusing `splitMinutesByDate`
 * (which already returns a single zero-minute bucket for a still-open shift
 * or inverted timestamps — see its own docstring). Summed across buckets so
 * a midnight-crossing shift still reports its FULL duration, not just one
 * day's portion.
 * @param {{ entrada_at: string, salida_at?: string | null }} fichada
 * @returns {number} exact minutes, never rounded (rounding is a display
 *   concern of the caller, same convention as `splitMinutesByDate` itself)
 */
export function minutosTrabajados(fichada) {
    return splitMinutesByDate(fichada).reduce((total, bucket) => total + bucket.minutos, 0);
}

/**
 * Summarizes a list of raw fichada rows (same shape
 * `js/infrastructure/fichadas.repository.js#listFichadas` returns) for the
 * "shown set" summary line above the Fichadas list.
 *
 * An `anulada` fichada is excluded from every count here — it stays
 * VISIBLE in the list itself (an admin voiding a fichada does not erase the
 * audit trail), but a voided shift never contributes to "how many fichadas"/
 * "how many are open"/"how much time was worked", same AD13-style
 * separation `LiquidacionView.js#renderMesConsumoPanel` already applies to
 * confirmed-vs-anulada liquidaciones.
 * @param {Array<{ entrada_at: string, salida_at?: string | null, anulada?: boolean }>} fichadas
 * @returns {{ total: number, abiertas: number, minutosTotal: number }}
 */
export function summarizeFichadas(fichadas) {
    const vigentes = (fichadas ?? []).filter((fichada) => !fichada.anulada);

    let abiertas = 0;
    let minutosTotal = 0;
    for (const fichada of vigentes) {
        if (fichada.salida_at == null) {
            abiertas += 1;
            continue;
        }
        minutosTotal += minutosTrabajados(fichada);
    }

    return { total: vigentes.length, abiertas, minutosTotal };
}

/** Abreviatura del día de la semana (0 = domingo), para "desde lun 21/09 22:00". */
const DIAS_SEMANA = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

/**
 * `"día dd/mm HH:mm"` en hora de Argentina (ART, UTC-3 fijo, sin DST) — la
 * misma aritmética de offset que `timesheet-policy.js`, nunca la zona del
 * navegador: el banner tiene que decir la misma hora que el reloj del local.
 * @param {string} iso
 * @returns {string}
 */
function formatDesdeArt(iso) {
    const shifted = new Date(new Date(iso).getTime() - ART_OFFSET_MINUTES * 60000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${DIAS_SEMANA[shifted.getUTCDay()]} ${pad(shifted.getUTCDate())}/${pad(shifted.getUTCMonth() + 1)} ${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`;
}

/**
 * Fichadas abiertas hace más de 12 h (F-B, owner decision 2026-09-27: el
 * banner del Panel Admin avisa de las salidas olvidadas). NO reimplementa el
 * umbral: delega en `detectAnomalies` (tipo `ABIERTA`), así el banner, la
 * lista de anomalías de Fichadas y el gate de liquidación nunca discrepan.
 * Más vieja primero (la más urgente arriba).
 * @param {Array<{ id: string, empleado_id: string, entrada_at: string, salida_at?: string|null, anulada?: boolean, empleados?: { nombre?: string } }>|null|undefined} fichadas
 * @param {string|number|Date} now
 * @returns {Array<{ fichadaId: string, empleadoId: string, nombre: string, entrada_at: string }>}
 */
export function jornadasAbiertasOlvidadas(fichadas, now) {
    const lista = fichadas ?? [];
    const abiertas = new Set(
        detectAnomalies(lista, { now })
            .filter((a) => a.tipo === ANOMALY_TYPES.ABIERTA)
            .map((a) => a.fichadaId),
    );
    return lista
        .filter((f) => abiertas.has(f.id))
        .sort((a, b) => new Date(a.entrada_at).getTime() - new Date(b.entrada_at).getTime())
        .map((f) => ({ fichadaId: f.id, empleadoId: f.empleado_id, nombre: f.empleados?.nombre ?? 'Empleado', entrada_at: f.entrada_at }));
}

/**
 * Texto del banner (F-B): "Hay N jornadas abiertas hace más de 12 h: Ana
 * (desde lun 21/09 22:00), …". `''` si no hay ninguna (el banner se oculta).
 * @param {Array<{ nombre: string, entrada_at: string }>} items salida de {@link jornadasAbiertasOlvidadas}
 * @returns {string}
 */
export function mensajeJornadasAbiertasOlvidadas(items) {
    if (!items || items.length === 0) return '';
    const cabecera = items.length === 1 ? 'Hay 1 jornada abierta hace más de 12 h' : `Hay ${items.length} jornadas abiertas hace más de 12 h`;
    const detalle = items.map((i) => `${i.nombre} (desde ${formatDesdeArt(i.entrada_at)})`).join(', ');
    return `${cabecera}: ${detalle}.`;
}

/**
 * Mensaje del rechazo 409 `jornadas_abiertas` de `api/liquidacion.js` (F-F,
 * owner decision 2026-09-27: no se confirma una liquidación con un turno sin
 * salida que toca el período). Una oración por turno abierto, todas listadas.
 * @param {Array<{ nombre: string, entrada_at: string }>} items
 * @returns {string}
 */
export function mensajeJornadasAbiertasLiquidacion(items) {
    const lista = items ?? [];
    const oraciones = lista.map((i) => `${i.nombre} tiene un turno sin salida desde el ${formatDesdeArt(i.entrada_at)}.`);
    return `${oraciones.join(' ')} ${lista.length === 1 ? 'Cerralo' : 'Cerralos'} antes de liquidar.`;
}

/**
 * Mensaje del rechazo 409 `fijo_ya_liquidado_mes` de `api/liquidacion.js`
 * (L10/D1, owner decisions 2026-09-27: un sueldo fijo se liquida una vez por
 * mes), y la nota de la fila del tablero que lo anticipa. El mes en palabras
 * sale de `formatMesLargo` (`'2026-09'` -> `'septiembre de 2026'`).
 * @param {string} nombre
 * @param {string} mes `YYYY-MM`
 * @returns {string}
 */
export function mensajeFijoYaLiquidadoMes(nombre, mes) {
    const [nombreMes, anio] = formatMesLargo(mes).split(' ');
    const mesEnPalabras = anio ? `${nombreMes} de ${anio}` : nombreMes;
    return `${nombre} ya tiene su sueldo fijo liquidado en ${mesEnPalabras}. Un sueldo fijo se liquida una vez por mes.`;
}

/**
 * Nota de la fila del tablero de Liquidación con turnos sin salida en el
 * período (F-F): avisa ANTES de confirmar lo que el servidor rechazaría.
 * `''` si no hay ninguno.
 * @param {Array<{ entrada_at: string }>|null|undefined} jornadas
 * @returns {string}
 */
export function notaTurnoSinSalida(jornadas) {
    if (!jornadas || jornadas.length === 0) return '';
    const desdes = jornadas.map((j) => `el ${formatDesdeArt(j.entrada_at)}`);
    if (desdes.length === 1) return `Turno sin salida desde ${desdes[0]}: cerralo antes de liquidar.`;
    return `Turnos sin salida desde ${desdes.slice(0, -1).join(', ')} y ${desdes.at(-1)}: cerralos antes de liquidar.`;
}

/**
 * Rango de fechas ART (`{ desde, hasta }`, inclusive) que toca una jornada —
 * F-E (owner decision 2026-09-27, guarda de plata). Mismo corte que el
 * sueldo: `splitMinutesByDate` (una jornada nocturna toca dos fechas; una
 * que termina justo a medianoche ART no toca la siguiente; una abierta o
 * invertida queda en la fecha de su entrada). `null` si la jornada no se
 * puede ubicar (sin entrada o timestamp ilegible): ese caso lo rechaza la
 * RPC por su cuenta, no esta guarda.
 *
 * PARIDAD: SQL `fichada_rango_art` (docs/supabase/32-fichadas-bloqueo-dia-liquidado.sql)
 * hace el mismo cálculo en la base; los dos TIENEN que coincidir.
 *
 * Una jornada abierta cuenta sólo en la fecha de su entrada: F-E pregunta
 * qué días liquidados cambiaría una EDICIÓN, y una abierta todavía no suma
 * minutos pagos. F-F (`jornadasAbiertasEnPeriodo`) en cambio la trata como
 * corriendo hasta ahora, porque pregunta si una salida posterior metería
 * minutos pagos en el período.
 * @param {{ entrada_at?: string|null, salida_at?: string|null }|null|undefined} jornada
 * @returns {{ desde: string, hasta: string }|null}
 */
export function rangoFechasJornada(jornada) {
    if (!jornada || jornada.entrada_at == null) return null;
    try {
        const buckets = splitMinutesByDate({ entrada_at: jornada.entrada_at, salida_at: jornada.salida_at ?? null });
        return { desde: buckets[0].fecha, hasta: buckets[buckets.length - 1].fecha };
    } catch {
        return null;
    }
}

/**
 * Primera liquidación CONFIRMADA (una anulada ya no protege nada) cuyo
 * período `[periodo_desde, periodo_hasta]` se superpone con alguno de los
 * `rangos` — cada rango por separado: el hueco entre el "antes" y el
 * "después" de una corrección no cuenta como tocado (F-E).
 * @param {Array<{ desde: string, hasta: string }|null>} rangos
 * @param {Array<{ periodo_desde: string, periodo_hasta: string, estado: string }>} liquidaciones
 * @returns {object|null}
 */
export function liquidacionQueCubre(rangos, liquidaciones) {
    const validos = (rangos ?? []).filter(Boolean);
    return (
        (liquidaciones ?? []).find(
            (l) => l.estado === 'confirmada' && validos.some((r) => r.desde <= l.periodo_hasta && r.hasta >= l.periodo_desde),
        ) ?? null
    );
}

/**
 * Mensaje del rechazo F-E, el MISMO para la guarda del cliente y para el
 * código `dia_liquidado` de la RPC (migración 32).
 * @param {string|null|undefined} nombre
 * @returns {string}
 */
export function mensajeDiaLiquidado(nombre) {
    return `Ese día ya está liquidado para ${nombre || 'el empleado'}. Para cambiar esta fichada, primero anulá la liquidación.`;
}
